import type { Cell, Clue, Puzzle } from "@shared/types.ts"

export interface CellClueInfo {
  across?: { number: number; startRow: number; startCol: number; length: number }
  down?: { number: number; startRow: number; startCol: number; length: number }
}

/**
 * Build a lookup from "row,col" -> which across/down clues pass through that cell.
 */
export function buildCellClueMap(puzzle: Puzzle): Map<string, CellClueInfo> {
  const map = new Map<string, CellClueInfo>()
  const grid = puzzle.grid
  const size = puzzle.gridSize

  // Find across word boundaries
  for (let r = 0; r < size; r++) {
    let startCol = -1
    for (let c = 0; c <= size; c++) {
      const isBlack = c >= size || grid[r]![c]!.black
      if (!isBlack && startCol === -1) {
        startCol = c
      } else if (isBlack && startCol !== -1) {
        const length = c - startCol
        if (length >= 3) {
          const number = grid[r]![startCol]!.number
          if (number !== undefined) {
            for (let cc = startCol; cc < c; cc++) {
              const key = `${r},${cc}`
              const info = map.get(key) ?? {}
              info.across = { number, startRow: r, startCol, length }
              map.set(key, info)
            }
          }
        }
        startCol = -1
      }
    }
  }

  // Find down word boundaries
  for (let c = 0; c < size; c++) {
    let startRow = -1
    for (let r = 0; r <= size; r++) {
      const isBlack = r >= size || grid[r]![c]!.black
      if (!isBlack && startRow === -1) {
        startRow = r
      } else if (isBlack && startRow !== -1) {
        const length = r - startRow
        if (length >= 3) {
          const number = grid[startRow]![c]!.number
          if (number !== undefined) {
            for (let rr = startRow; rr < r; rr++) {
              const key = `${rr},${c}`
              const info = map.get(key) ?? {}
              info.down = { number, startRow, startCol: c, length }
              map.set(key, info)
            }
          }
        }
        startRow = -1
      }
    }
  }

  return map
}

/**
 * Get all cells that belong to the same word as (row, col) in the given direction.
 */
export function getWordCells(
  puzzle: Puzzle,
  row: number,
  col: number,
  direction: "across" | "down",
  cellClueMap: Map<string, CellClueInfo>,
): [number, number][] {
  const info = cellClueMap.get(`${row},${col}`)
  if (!info) return []

  const wordInfo = direction === "across" ? info.across : info.down
  if (!wordInfo) return []

  const cells: [number, number][] = []
  if (direction === "across") {
    for (let c = wordInfo.startCol; c < wordInfo.startCol + wordInfo.length; c++) {
      cells.push([wordInfo.startRow, c])
    }
  } else {
    for (let r = wordInfo.startRow; r < wordInfo.startRow + wordInfo.length; r++) {
      cells.push([r, wordInfo.startCol])
    }
  }
  return cells
}
